import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { Gift, User, Buildings, Crown } from "@phosphor-icons/react";
import { AuthHero } from "@/components/AuthHero";

const INPUT = "mt-1.5 w-full rounded-xl border border-white/10 bg-[#111a2e] px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#FF7A00]";

const TYPES = [
  { key: "personal", label: "Personal", icon: User, desc: "Free individual account" },
  { key: "business", label: "Business", icon: Buildings, desc: "Organization + Cloud PBX" },
  { key: "enterprise", label: "Enterprise", icon: Crown, desc: "Advanced, higher limits" },
];

export default function Register() {
  const { registerStart, registerVerify, error, setError } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const rawNext = params.get("next") || "";
  const nextUrl = /^\/(?!\/)/.test(rawNext) ? rawNext : "";
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "", phone: "" });
  const [type, setType] = useState(params.get("type") === "business" ? "business" : "personal");
  const [inspector, setInspector] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form");   // form | otp
  const [channel, setChannel] = useState("email");
  const [challengeId, setChallengeId] = useState("");
  const [hint, setHint] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const goAfter = () => {
    if (nextUrl) { navigate(nextUrl); return; }
    if (type === "business") navigate("/app/business");
    else if (type === "enterprise") navigate("/app/business?intent=enterprise");
    else navigate("/welcome");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError("Please agree to the Terms of Service and Privacy Policy to continue."); return; }
    setBusy(true);
    try {
      const d = await registerStart({ ...form, ref, role: inspector ? "inspector" : undefined });
      setChallengeId(d.challenge_id); setChannel(d.channel || "email"); setHint(d.hint || form.phone); setStage("otp"); setCode(""); setCooldown(30);
    } catch {} finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d+$/.test(code.trim())) { setError("Enter the verification code we sent"); return; }
    setBusy(true);
    try {
      await registerVerify(challengeId, code.trim());
      goAfter();
    } catch {} finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const d = await registerStart({ ...form, ref, role: inspector ? "inspector" : undefined });
      setChallengeId(d.challenge_id); setCode(""); setCooldown(30);
    } catch {} finally { setBusy(false); }
  };

  const showOrg = type === "business" || type === "enterprise";

  return (
    <div className="grid min-h-screen bg-[#070B16] text-white md:grid-cols-2">
      <div className="flex items-center justify-center bg-[#070B16] p-6 text-white md:order-2">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up">
          <h1 className="font-display text-3xl font-semibold tracking-tight">Create your account</h1>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FF7A00]/15 px-3 py-1 text-xs font-semibold text-[#FF7A00]">
            <Gift size={14} weight="fill" /> {ref ? "You were invited — a welcome bonus is waiting" : "Get $1 free credit · calls, chat, top-ups & more"}
          </div>

          {error && <div data-testid="register-error" className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>}

          {stage === "form" ? (
          <>
          <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-slate-400">Account type</label>
          <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="register-account-type">
            {TYPES.map((t) => (
              <button key={t.key} type="button" data-testid={`type-${t.key}`} onClick={() => setType(t.key)}
                className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-colors ${type === t.key ? "border-[#FF7A00] bg-[#FF7A00]/10 text-[#FF7A00]" : "border-white/10 text-slate-400 hover:border-[#FF7A00]/50"}`}>
                <t.icon size={20} weight="fill" />
                <span className="text-xs font-semibold">{t.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">{TYPES.find((t) => t.key === type)?.desc}. {showOrg && "You'll set up your organization after signing up."}</p>

          {[
            { k: "name", label: "Full name", type: "text", ph: "Alex Rivera" },
            ...(showOrg ? [{ k: "organization", label: "Business name", type: "text", ph: "Acme Corp" }] : []),
            { k: "email", label: "Email", type: "email", ph: "you@company.com" },
            { k: "phone", label: "Mobile number", type: "tel", ph: "+1 202 555 0123" },
            { k: "password", label: "Password", type: "password", ph: "Min 6 characters" },
          ].map((f) => (
            <div key={f.k}>
              <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-400">{f.label}</label>
              <input data-testid={`register-${f.k}`} type={f.type} required value={form[f.k]} onChange={set(f.k)}
                className={INPUT} placeholder={f.ph} />
              {f.k === "phone" && <p className="mt-1 text-[11px] text-slate-500">We'll text a verification code to confirm this number. Standard rates may apply.</p>}
            </div>
          ))}

          <label className="mt-5 flex items-start gap-2 text-xs text-slate-400" data-testid="register-consent-label">
            <input type="checkbox" data-testid="register-consent" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-[#111a2e]" />
            <span>I agree to the <Link to="/terms" target="_blank" rel="noreferrer" className="font-semibold text-[#FF7A00] hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-[#FF7A00] hover:underline">Privacy Policy</Link>.</span>
          </label>

          <button data-testid="register-submit" type="submit" disabled={busy || !agree}
            className="mt-4 w-full rounded-full bg-[#FF7A00] py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
            {busy ? "Sending code…" : "Continue"}
          </button>

          <label className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
            <input type="checkbox" data-testid="register-inspector" checked={inspector} onChange={(e) => setInspector(e.target.checked)} className="h-3.5 w-3.5" />
            Applying as a field Inspector (requires admin approval)
          </label>
          </>
          ) : (
          <div data-testid="register-otp" className="mt-6">
            <p className="text-sm text-slate-300">Enter the verification code we {channel === "sms" ? "texted" : "emailed"} to <span className="font-semibold text-white">{hint}</span>.</p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-400">Verification code</label>
            <input data-testid="register-otp-code" inputMode="numeric" maxLength={10} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); verify(); } }}
              className={INPUT + " text-center font-mono-metric tracking-[0.5em]"} placeholder="••••••" autoFocus />
            <button type="button" data-testid="register-verify" onClick={verify} disabled={busy}
              className="mt-6 w-full rounded-full bg-[#FF7A00] py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & create account"}
            </button>
            <div className="mt-3 flex items-center justify-center gap-4 text-xs text-slate-400">
              <button type="button" data-testid="register-otp-back" onClick={() => { setStage("form"); setError(""); }} className="hover:text-white">← Edit details</button>
              <button type="button" data-testid="register-otp-resend" onClick={resend} disabled={busy || cooldown > 0} className="font-semibold text-[#FF7A00] disabled:opacity-60">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
            </div>
          </div>
          )}

          <p className="mt-4 text-center text-sm text-slate-400">
            Already have an account? <Link to="/login" data-testid="to-login" className="font-semibold text-[#FF7A00]">Sign in</Link>
          </p>
        </form>
      </div>

      <AuthHero />
    </div>
  );
}
